import { axiosInstance } from './axiosInstance';

const LOGS_BASE = '/api/safety-alert-dispatch-logs';

/** 같은 창: CustomEvent · 다른 창(통합관제 팝업 등): storage 이벤트 */
export const SAFETY_ALERT_DISPATCH_LOG_UPDATED_EVENT = 'safety-alert-dispatch-log-updated';
export const SAFETY_ALERT_DISPATCH_LOG_SYNC_STORAGE_KEY = 'safety-alert-dispatch-log-updated';

export function notifySafetyAlertDispatchLogUpdated(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(SAFETY_ALERT_DISPATCH_LOG_UPDATED_EVENT));
  try {
    localStorage.setItem(SAFETY_ALERT_DISPATCH_LOG_SYNC_STORAGE_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export type SafetyAlertDispatchLogItem = {
  id: number;
  pool_id: number;
  pool_name?: string | null;
  event_id?: number | null;
  channel: string;
  channel_label: string;
  message: string;
  alert_level: string;
  success: boolean;
  detail_summary?: string | null;
  detail_json?: string | null;
  image_url?: string | null;
  created_at: string;
};

export type SafetyAlertDispatchLogListResponse = {
  items: SafetyAlertDispatchLogItem[];
  total: number;
};

export const safetyAlertDispatchService = {
  list: async (params?: {
    poolId?: number;
    eventId?: number;
    limit?: number;
    offset?: number;
  }): Promise<SafetyAlertDispatchLogListResponse> => {
    const res = await axiosInstance.get<SafetyAlertDispatchLogListResponse>(LOGS_BASE, {
      params: {
        pool_id: params?.poolId,
        event_id: params?.eventId,
        limit: params?.limit ?? 100,
        offset: params?.offset ?? 0,
      },
    });
    const data = res.data;
    const items = Array.isArray(data?.items) ? data.items : [];
    return {
      items,
      total: Number(data?.total) || items.length,
    };
  },

  eventSummary: async (
    eventIds: number[],
    poolId?: number,
  ): Promise<Record<number, number>> => {
    const ids = eventIds.filter((id) => Number.isFinite(id) && id > 0);
    if (ids.length === 0) return {};
    try {
      const res = await axiosInstance.get<{ counts: Record<string, number> }>(
        `${LOGS_BASE}/event-summary`,
        {
          params: {
            event_ids: ids.join(','),
            ...(poolId != null && Number.isFinite(poolId) ? { pool_id: poolId } : {}),
          },
        },
      );
      const out: Record<number, number> = {};
      const raw = res.data?.counts || {};
      for (const [k, v] of Object.entries(raw)) {
        const n = Number(k);
        if (Number.isFinite(n)) out[n] = Number(v) || 0;
      }
      return out;
    } catch {
      return {};
    }
  },

  deleteOne: async (logId: number, poolId?: number): Promise<void> => {
    await axiosInstance.delete(`${LOGS_BASE}/${logId}`, {
      params: poolId != null ? { pool_id: poolId } : undefined,
    });
  },

  bulkDelete: async (options: {
    ids?: number[];
    all?: boolean;
    poolId?: number;
  }): Promise<number> => {
    const res = await axiosInstance.post<{ deleted?: number }>(`${LOGS_BASE}/bulk-delete`, {
      ids: options.ids,
      all: options.all ?? false,
      pool_id: options.poolId,
    });
    return Number(res.data?.deleted ?? 0);
  },
};

export function resolveEventDbId(event: {
  ack_id?: string | number;
  metadata?: Record<string, unknown>;
}): number | null {
  const fromMeta = event.metadata?.event_db_id;
  if (fromMeta != null && Number.isFinite(Number(fromMeta))) return Number(fromMeta);
  const metaId = event.metadata?.id;
  if (metaId != null && Number.isFinite(Number(metaId))) return Number(metaId);
  const ack = event.ack_id;
  if (ack != null && Number.isFinite(Number(ack))) return Number(ack);
  return null;
}
